import { Box, IconButton, TableCell, TableRow, ThemeProvider, Typography } from '@mui/material';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import FHConfirmDialog from './dialogs/FHConfirmDialog';
import FHAntdDateTimePicker from './FHAntdDateTimePicker';
import FHExcelInput from './FHExcelFields/FHExcelInput';
import FHExcelSelect from './FHExcelFields/FHExcelSelect';
import { API_DATE_FORMAT, DATE_DISPLAY_FORMAT, Themes } from '../../constants';
import { disabledFutureDate } from '../../helpers';
import { FORTA_BLUE, FORTA_GRAY2 } from '../../utils/colors';
import { getTFHExcelTableTheme } from '../../utils/theme';
import CreateInvoiceIcon from '../svgs/CreateInvoiceIcon';
import RemoveIcon from './../svgs/RemoveIcon';
import SaveIcon from './../svgs/SaveIcon';

import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import type { Moment } from 'moment';
import type { SelectValueType } from '../../utils/types';

type TABLE_ACTIONS = 'save' | 'delete' | 'edit';

const FHExcelTableContext = createContext<
  | {
      isEditable: boolean;
      editingRowIndex: number | null;
      maxNumericInputValue: number;
      handleEditRow: (rowIndex: number, skipDisclaimer?: boolean) => void;
      handleUpdateRow: (data: any) => void;
      handleDeleteRow: (data: any) => void;
      disableRowCriteria?: (data: any) => boolean;
    }
  | undefined
>(undefined);

const useExcelTableContent = () => {
  const context = useContext(FHExcelTableContext);
  if (!context) {
    throw new Error('useExcelTableContent must be used within a FHExcelTableContext.Provider');
  }
  return context;
};

export enum FHExcelCellInputComponentTypes {
  SELECT = 'select',
  INPUT = 'input',
  DATE_PICKER = 'datepicker',
}
export interface FHExcelTableColumnDef extends MUIDataTableColumn {
  component?: FHExcelCellInputComponentTypes | null;
  isFloatValueColumn?: boolean;
  dropdownOptions?: { value: number | string; label: string }[];
  valueFormatter?: (val: any) => any;
  isMandatory?: boolean;
}

type ITableData = {
  id: string | number | null;
};

type ICellValue = { display: boolean; value: any };
interface IFHExcelTableProps {
  colorTheme?: Themes;
  options?: MUIDataTableOptions;
  // data: An array of objects, property in individual objects corresponds to columns
  // Individual object propeties should match with columns defined in colDefs or else
  // they might be dropped by the FHExcelTable component
  // id is required to track individual rows, only only row can have id = null at a time, to track new row with id = null
  data: ITableData[];
  colDefs: FHExcelTableColumnDef[];
  deleteRowMessage?: string;
  editRowMessage?: string;
  saveRowMessage?: string;
  isEditable?: boolean;
  maxNumericInputValue?: number;
  onSave: (rowData: any) => Promise<boolean>;
  onDelete: (rowData: any) => Promise<boolean>;
  discardUnsaved: () => void;
  disableRowCriteria?: (rowData: any) => boolean;
}

interface IFHExcelTableRowProps {
  data: any;
  rowIndex: number;
  colDefs: FHExcelTableColumnDef[];
}

interface IFHExcelTableEditableProps {
  isReadOnly: boolean;
  data: any;
  rowIndex: number;
  colDefs: FHExcelTableColumnDef[];
}

interface IFHExcelTableReadOnlyProps {
  isReadOnly: boolean;
  data: any;
  rowIndex: number;
  colDefs: FHExcelTableColumnDef[];
  toggleMode: () => void;
}

function FHExcelTableReadOnlyRow({
  isReadOnly,
  data,
  colDefs,
  toggleMode,
}: IFHExcelTableReadOnlyProps) {
  const { editingRowIndex, handleDeleteRow, disableRowCriteria } = useExcelTableContent();
  const [editingThisRow, setEditingThisRow] = useState<boolean>(false);

  const rowData: any = {};
  for (const key in data) {
    rowData[key] = data[key].value;
  }

  const handleDelete = () => {
    handleDeleteRow(rowData);
  };

  return (
    <TableRow
      sx={{
        '&:hover': {
          td: {
            div: {
              button: {
                visibility: 'visible !important',
              },
            },
          },
        },
      }}
    >
      {isReadOnly ? (
        <></>
      ) : (
        <TableCell>
          {(!disableRowCriteria || !disableRowCriteria(rowData)) && (
            <Box sx={{ display: 'flex' }}>
              <IconButton
                sx={{
                  visibility: 'hidden',
                  opacity: editingRowIndex !== null && !editingThisRow ? 0 : 1,
                }}
                disabled={editingRowIndex !== null}
                onClick={() => {
                  toggleMode();
                  setEditingThisRow(true);
                }}
                data-testid="editExistingPayment"
              >
                <CreateInvoiceIcon width={18} height={18} />
              </IconButton>
              <IconButton
                onClick={handleDelete}
                sx={{
                  visibility: 'hidden',
                  opacity: editingRowIndex !== null && !editingThisRow ? 0 : 1,
                }}
                disabled={editingRowIndex !== null}
                data-testid="deleteExistingPayment"
              >
                <RemoveIcon width={18} height={18} />
              </IconButton>
            </Box>
          )}
        </TableCell>
      )}
      {colDefs.map((item) =>
        data[item.name].display ? (
          <TableCell key={item.name} data-testid={`${item.name}_readonly`}>
            <Typography
              textAlign={item.isFloatValueColumn ? 'right' : 'left'}
              pr={item.isFloatValueColumn ? '30px' : 0}
            >
              {item.valueFormatter
                ? item.valueFormatter(data[item.name].value)
                : data[item.name].value}
            </Typography>
          </TableCell>
        ) : null,
      )}
    </TableRow>
  );
}

function FHExcelTableEditableRow({ isReadOnly, data, colDefs }: IFHExcelTableEditableProps) {
  const { maxNumericInputValue, handleUpdateRow, handleDeleteRow } = useExcelTableContent();
  const [rowState, setRowState] = useState<any>({});

  useEffect(() => {
    if (!data) return;
    setRowState(data);
  }, []);

  const handleFieldUpdate = (field: string, value: any) => {
    const data = { ...rowState, [field]: { ...rowState[field], value: value ? value : null } };
    setRowState(data);
  };

  const handleDateUpdate = (field: string, value: Moment) => {
    const data = {
      ...rowState,
      [field]: { ...rowState[field], value: moment(value).format(API_DATE_FORMAT) },
    };
    setRowState(data);
  };

  const handleSave = () => {
    const rowData: any = {};
    for (const key in rowState) {
      const columnDef = colDefs.find((col) => col.name === key);
      // Validate amount field value
      const amountVal = parseFloat(rowState['amount'].value ?? '0');
      if (amountVal > maxNumericInputValue) {
        toast.error(`Amount cannot be greater than ${maxNumericInputValue}.`);
        return;
      }
      if (columnDef?.isMandatory && !rowState[key].value) {
        toast.error(`${columnDef.label} is required`);
        return;
      }
      rowData[key] = rowState[key].value;
    }
    handleUpdateRow(rowData);
  };

  const handleDelete = () => {
    const rowData: any = {};
    for (const key in rowState) {
      rowData[key] = rowState[key].value;
    }
    handleDeleteRow(rowData);
  };

  const handleAmountUpdate = (field: string, value: string) => {
    if (!value) {
      setRowState((prev: any) => ({
        ...prev,
        [field]: { ...rowState[field], value: null },
      }));
      return;
    }
    // Allow only 2 decimal places
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      return;
    }
    setRowState((prev: any) => ({
      ...prev,
      [field]: { ...rowState[field], value: value ? value : null },
    }));
  };

  return (
    <TableRow>
      {isReadOnly ? (
        <></>
      ) : (
        <TableCell
          style={{
            whiteSpace: 'nowrap',
            position: 'sticky',
            left: 0,
            background: 'white',
            zIndex: 101,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <IconButton onClick={handleSave} data-testid="savePaymentMode">
              <SaveIcon width={16} height={16} color={FORTA_BLUE.primary} />
            </IconButton>
            <IconButton onClick={handleDelete} data-testid="deletePaymentMode">
              <RemoveIcon width={18} height={18} />
            </IconButton>
          </Box>
        </TableCell>
      )}
      {colDefs.map((item) => {
        return data[item.name].display ? (
          <TableCell key={item.name} sx={{ paddingRight: '30px' }}>
            {item.component === FHExcelCellInputComponentTypes.INPUT && (
              <FHExcelInput
                data-testid={item.name}
                sx={{
                  '.MuiOutlinedInput-input': {
                    textAlign: 'right',
                  },
                  mt: 0.3,
                }}
                inputType={item.name === 'amount' ? 'number' : 'text'}
                maxNumericValue={maxNumericInputValue}
                value={rowState[item.name]?.value ?? ''}
                error={
                  item.name === 'amount' &&
                  parseFloat(rowState[item.name]?.value ?? '0') > maxNumericInputValue
                }
                helperText={
                  item.name === 'amount' &&
                  parseFloat(rowState[item.name]?.value ?? '0') > maxNumericInputValue
                    ? `Max: $${maxNumericInputValue}`
                    : ''
                }
                onChange={(e) => {
                  if (item.isFloatValueColumn) {
                    handleAmountUpdate(`${item.name}`, e.target.value);
                  } else {
                    handleFieldUpdate(`${item.name}`, e.target.value);
                  }
                }}
              />
            )}
            {item.component === FHExcelCellInputComponentTypes.SELECT && (
              <FHExcelSelect
                data-testid={item.name}
                fullWidth
                value={rowState[item.name]?.value ?? ''}
                options={item.dropdownOptions ?? []}
                onUpdate={(value: SelectValueType) => handleFieldUpdate(`${item.name}`, value)}
              />
            )}
            {item.component === FHExcelCellInputComponentTypes.DATE_PICKER && (
              <FHAntdDateTimePicker
                data-testid={item.name}
                inputHeight={27}
                sx={{
                  width: '100%',
                  minWidth: '120px',
                  boxShadow: 'none',
                  border: `1px solid ${FORTA_GRAY2}`,
                  borderRadius: '4px',
                  '&:focus-within': {
                    border: `1px solid ${FORTA_GRAY2}`,
                  },
                  '&:hover': {
                    border: `1px solid ${FORTA_GRAY2}`,
                  },
                }}
                timezoneSx={{
                  py: 0.3,
                  px: 0.5,
                  height: 27,
                  borderRadius: '0 4px 4px 0',
                }}
                iconWidth={16}
                iconHeight={16}
                format={DATE_DISPLAY_FORMAT}
                allowClear={false}
                disabledDate={disabledFutureDate}
                disabledIconColor={FORTA_GRAY2}
                value={
                  rowState[item.name]?.value ? moment(rowState[item.name]?.value, 'YYYY-MM-DD') : ''
                }
                onSelect={(value: Moment) => handleDateUpdate(`${item.name}`, value)}
                error={false}
              />
            )}
            {!item.component && (
              <Typography>
                {item.valueFormatter
                  ? item.valueFormatter(data[item.name].value)
                  : data[item.name].value}
              </Typography>
            )}
          </TableCell>
        ) : null;
      })}
    </TableRow>
  );
}

function FHExcelTableRow({ data, rowIndex, colDefs }: IFHExcelTableRowProps) {
  const { isEditable, editingRowIndex, handleEditRow } = useExcelTableContent();

  const editMode = editingRowIndex !== null && editingRowIndex === rowIndex;

  const handleModeChange = () => {
    handleEditRow(rowIndex);
  };

  return editMode ? (
    <FHExcelTableEditableRow
      rowIndex={rowIndex}
      data={data}
      colDefs={colDefs}
      isReadOnly={!isEditable}
    />
  ) : (
    <FHExcelTableReadOnlyRow
      rowIndex={rowIndex}
      data={data}
      colDefs={colDefs}
      isReadOnly={!isEditable}
      toggleMode={handleModeChange}
    />
  );
}

const getFHExcelTableOption = (
  colDefs: FHExcelTableColumnDef[],
  options: MUIDataTableOptions = {},
  isEditable: boolean,
) => ({
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: false,
  download: false,
  responsive: 'standard' as const,
  selectableRows: 'none' as const,
  textLabels: {
    body: {
      noMatch: 'No rows',
    },
  },
  customRowRender: (data: any[], dataIndex: number, rowIndex: number) => {
    const values: { [name: string]: ICellValue } = {};
    const indexOffset = isEditable ? 1 : 0; // adding 1 to accomodate 'actions' column
    // options.display is an optional value, mui treat it as true it doesn't exists
    // so an explicit comparision with boolean value false is required

    colDefs.forEach((col, i) => {
      values[col.name] = {
        value: data[i + indexOffset],
        display: col.options?.display === false ? false : true,
      };
    });
    return (
      <FHExcelTableRow
        key={values.id ? values.id.value : dataIndex}
        data={values}
        colDefs={colDefs}
        rowIndex={rowIndex}
      />
    );
  },
  ...options,
});

function FHExcelTable({
  colorTheme = Themes.Primary,
  data,
  colDefs,
  options,
  deleteRowMessage,
  editRowMessage,
  saveRowMessage,
  isEditable = false,
  maxNumericInputValue = 10000000, // 10M
  onSave,
  onDelete,
  discardUnsaved,
  disableRowCriteria,
}: IFHExcelTableProps) {
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [updatedValues, setUpdatedValues] = useState<any[]>(data);
  const [showDeleteDisclaimer, setShowDeleteDisclaimer] = useState<boolean>(false);
  const [showSaveDisclaimer, setShowSaveDisclaimer] = useState<boolean>(false);
  const [itemBeingModified, setItemBeingModified] = useState<{
    rowData?: ITableData;
    action: TABLE_ACTIONS;
  } | null>(null);

  // To load async data.
  // Problem: It will reset the data, if parent rerenders with updated data
  useEffect(() => {
    setUpdatedValues(data);
    setEditingRowIndex(null);
    const newRowIndex = data?.findIndex((row) => row.id === null);
    if (newRowIndex > -1) {
      setEditingRowIndex(newRowIndex);
    } else {
      setEditingRowIndex(null);
    }
  }, [data]);

  const handleUpdateRow = (rowData: any) => {
    if (!saveRowMessage) {
      onSave(rowData);
      return;
    }
    setItemBeingModified({ rowData, action: 'save' });
    setShowSaveDisclaimer(true);
  };

  const handleDeleteRow = (rowData: any) => {
    if (!deleteRowMessage) {
      onDelete(rowData);
      return;
    }
    setItemBeingModified({ rowData, action: 'delete' });
    setShowDeleteDisclaimer(true);
  };

  const handleEditRow = (rowIndex: number, skipDisclaimer?: boolean) => {
    if (editRowMessage && !skipDisclaimer) {
      setEditingRowIndex(null);
    } else {
      setEditingRowIndex(rowIndex);
    }
  };

  return (
    <>
      <FHExcelTableContext.Provider
        value={{
          isEditable,
          editingRowIndex,
          handleUpdateRow,
          handleDeleteRow,
          handleEditRow,
          disableRowCriteria,
          maxNumericInputValue,
        }}
      >
        <ThemeProvider theme={getTFHExcelTableTheme(colorTheme)}>
          <Box
            style={{
              overflowX: 'auto',
              width: '100%',
            }}
          >
            <MUIDataTable
              title={''}
              data={updatedValues}
              columns={
                isEditable
                  ? [
                      {
                        name: '',
                        options: {
                          setCellHeaderProps: () => ({
                            style: {
                              width: '100px',
                            },
                          }),
                          setCellProps: () => ({
                            style: {
                              whiteSpace: 'nowrap',
                              position: 'sticky',
                              left: '0',
                              background: 'white',
                              zIndex: 100,
                            },
                          }),
                        },
                      },
                      ...colDefs,
                    ]
                  : colDefs
              }
              options={{ ...getFHExcelTableOption(colDefs, options, isEditable) }}
            />
          </Box>
        </ThemeProvider>
      </FHExcelTableContext.Provider>
      <FHConfirmDialog
        open={showDeleteDisclaimer}
        content={deleteRowMessage}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        submittext="Delete"
        canceltext="Cancel"
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowDeleteDisclaimer(false);
          setItemBeingModified(null);
        }}
        onSubmit={async () => {
          await onDelete(itemBeingModified?.rowData);
          setShowDeleteDisclaimer(false);
          setItemBeingModified(null);
          setEditingRowIndex(null);
        }}
        submitBtnTestId="Delete"
        cancelBtnTestId="Cancel"
      />

      <FHConfirmDialog
        open={showSaveDisclaimer}
        content={saveRowMessage}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        submittext="Save"
        canceltext="Discard changes"
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowSaveDisclaimer(false);
          setItemBeingModified(null);
          setEditingRowIndex(null);
          discardUnsaved();
        }}
        onSubmit={async () => {
          await onSave(itemBeingModified?.rowData);
          setShowSaveDisclaimer(false);
        }}
        submitBtnTestId="save"
        cancelBtnTestId="cancel"
      />
    </>
  );
}

export default FHExcelTable;
